import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { eventsApi } from '../../lib/api';

const ROLE_OPTIONS = [
  'Retailer (looking to buy wholesale)',
  'Manufacturer (looking to license)',
  'Agent (looking for talent to represent)',
  'Other',
];

export default function CompanyIntakeForm({ event }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [sponsorshipLoading, setSponsorshipLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', company_name: '', role: '',
    logo_url: '',
    additional_guests: [''],
    categories_selected: [],
    additional_guidelines: '',
    requires_nda: false,
    nda_instructions: '',
    phone_number: '',
    sms_consent: false,
    terms_accepted: false,
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const toggleCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      categories_selected: prev.categories_selected.includes(cat)
        ? prev.categories_selected.filter(c => c !== cat)
        : [...prev.categories_selected, cat],
    }));
  };

  const updateGuest = (index, val) => {
    setForm(prev => {
      const guests = [...prev.additional_guests];
      guests[index] = val;
      return { ...prev, additional_guests: guests };
    });
  };

  const addGuest = () => setForm(prev => ({ ...prev, additional_guests: [...prev.additional_guests, ''] }));
  const removeGuest = (index) => setForm(prev => ({ ...prev, additional_guests: prev.additional_guests.filter((_, i) => i !== index) }));

  const handleLogoUpload = async (file) => {
    if (!file) return;
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, JPG, SVG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Logo must be under 5MB');
      return;
    }

    setLogoUploading(true);
    const ext = file.name.split('.').pop().toLowerCase();
    const path = `company-logos/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

    const { error } = await supabase.storage.from('event-assets').upload(path, file, {
      contentType: file.type,
      cacheControl: '3600',
    });

    if (error) {
      console.error('Logo upload error:', error);
      alert('Upload failed: ' + error.message);
      setLogoUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('event-assets').getPublicUrl(path);
    update('logo_url', urlData.publicUrl);
    setLogoUploading(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const { data, error } = await supabase.from('event_company_submissions').insert({
      event_id: event.id,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      company_name: form.company_name,
      role: form.role,
      logo_url: form.logo_url || null,
      additional_guests: form.additional_guests.filter(g => g),
      categories_selected: form.categories_selected,
      additional_guidelines: form.additional_guidelines,
      requires_nda: form.requires_nda,
      nda_instructions: form.nda_instructions,
      phone_number: form.phone_number,
      sms_consent: form.sms_consent,
    }).select().single();

    if (error) {
      alert('Submission failed: ' + error.message);
    } else {
      setSubmitted(true);
      setSubmissionId(data?.id);
      if (hasSponsorshipStep) {
        setStep(5);
      } else {
        setStep(6);
      }
    }
    setSubmitting(false);
  };

  const handleSponsorshipCheckout = async (tier) => {
    setSponsorshipLoading(true);
    try {
      const { url } = await eventsApi.checkoutSponsorship(event.pitch_event_id || event.id, {
        tier_id: tier.id,
        tier_name: tier.name,
        amount_cents: tier.price_cents,
        company_name: form.company_name,
        logo_url: form.logo_url || null,
        intake_submission_id: submissionId,
      });
      window.location.href = url;
    } catch (err) {
      alert('Checkout failed: ' + err.message);
      setSponsorshipLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-sm focus:border-amber-500 focus:outline-none transition-colors";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1.5";

  const categories = event.intake_categories || [
    'Arts + Crafts / Girls Activity', 'Action Figures', 'Fashion Dolls', 'Pre-School',
    'Electronics', 'Family Games', 'Party Games', 'Sports / Outdoor Play',
    'Feature Plush', 'Collectibles', 'Show / IP Concepts', 'Books',
  ];

  const hasSponsorshipStep = event.sponsorship_enabled && event.sponsorship_tiers?.length > 0;
  const stepLabels = [
    'Book Time',
    'Contact Info',
    'Pitch Preferences',
    'NDA & Agreements',
    ...(hasSponsorshipStep ? ['Sponsorship'] : []),
    'Confirmation',
  ];

  return (
    <div>
      {/* Progress Bar */}
      <div className="flex items-center gap-2 mb-10">
        {stepLabels.map((s, i) => (
          <div key={i} className="flex-1">
            <div className={`h-1.5 rounded-full transition-colors ${i + 1 <= step ? 'bg-amber-500' : 'bg-gray-200'}`} />
            <p className={`text-xs mt-2 ${i + 1 === step ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>{s}</p>
          </div>
        ))}
      </div>

      {/* STEP 1: Calendly Booking (first) */}
      {step === 1 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>
            {event.booking_cta_text || 'Reserve Your Time Slot'}
          </h2>
          <p className="text-sm text-gray-500">Start by selecting your preferred date and time. You'll complete your registration details next.</p>

          {event.calendly_booking_link ? (
            <div>
              <iframe
                src={event.calendly_booking_link}
                width="100%"
                height="700"
                frameBorder="0"
                title="Book a time"
                className="rounded-2xl border border-gray-200"
              />
              <button
                onClick={() => setStep(2)}
                className="w-full mt-6 px-6 py-3.5 rounded-full text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
              >
                I've Booked My Time — Continue to Registration
              </button>
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-gray-500 mb-4">Time slot booking is not yet available for this event.</p>
              <button
                onClick={() => setStep(2)}
                className="px-6 py-3.5 rounded-full text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
              >
                Continue to Registration
              </button>
            </div>
          )}
        </div>
      )}

      {/* STEP 2: Contact Info */}
      {step === 2 && (
        <div className="space-y-5">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>Contact Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>First Name *</label>
              <input className={inputCls} value={form.first_name} onChange={e => update('first_name', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Last Name *</label>
              <input className={inputCls} value={form.last_name} onChange={e => update('last_name', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Email *</label>
            <input type="email" className={inputCls} value={form.email} onChange={e => update('email', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Company Name *</label>
            <input className={inputCls} value={form.company_name} onChange={e => update('company_name', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Company Logo</label>
            <p className="text-xs text-gray-400 mb-2">PNG, JPG, SVG, or WebP — max 5MB. Used on the event page if you sponsor.</p>
            {form.logo_url ? (
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl border border-gray-200 bg-white p-2 flex items-center justify-center overflow-hidden">
                  <img src={form.logo_url} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-medium">Logo uploaded</p>
                  <button onClick={() => update('logo_url', '')} className="text-xs text-gray-400 hover:text-red-500 mt-1">Remove</button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={e => handleLogoUpload(e.target.files[0])}
                  disabled={logoUploading}
                  className={inputCls}
                  style={{ padding: 10 }}
                />
                {logoUploading && <p className="text-xs text-amber-600 mt-1">Uploading...</p>}
              </div>
            )}
          </div>
          <div>
            <label className={labelCls}>Role at Company *</label>
            <select className={inputCls} value={form.role} onChange={e => update('role', e.target.value)}>
              <option value="">Select a role...</option>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Additional Guests</label>
            {form.additional_guests.map((g, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <input type="email" className={inputCls} placeholder="guest@company.com" value={g} onChange={e => updateGuest(i, e.target.value)} />
                {form.additional_guests.length > 1 && (
                  <button onClick={() => removeGuest(i)} className="text-red-400 text-sm font-semibold px-2">✕</button>
                )}
              </div>
            ))}
            <button onClick={addGuest} className="text-amber-600 text-sm font-semibold">+ Add Guest</button>
          </div>

          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="flex-1 px-6 py-3.5 rounded-full text-sm font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50">Back</button>
            <button
              onClick={() => setStep(3)}
              disabled={!form.first_name || !form.last_name || !form.email || !form.company_name || !form.role}
              className="flex-1 px-6 py-3.5 rounded-full text-sm font-semibold text-white disabled:opacity-50 transition-all"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Pitch Preferences */}
      {step === 3 && (
        <div className="space-y-5">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>Pitch Preferences</h2>
          <p className="text-sm text-gray-500 mb-4">Select the categories you'd like to see pitches for:</p>
          <div className="grid grid-cols-2 gap-3">
            {categories.map(cat => (
              <label key={cat} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                form.categories_selected.includes(cat) ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="checkbox"
                  checked={form.categories_selected.includes(cat)}
                  onChange={() => toggleCategory(cat)}
                  className="w-4 h-4 accent-amber-500"
                />
                <span className="text-sm text-gray-700">{cat}</span>
              </label>
            ))}
          </div>
          <div>
            <label className={labelCls}>Additional Guidelines for the Organizer (optional)</label>
            <textarea className={inputCls} value={form.additional_guidelines} onChange={e => update('additional_guidelines', e.target.value)} rows={3} placeholder="Any specific types of products you're most interested in?" />
          </div>
          <div className="flex gap-3">
            <button onClick={() => setStep(2)} className="flex-1 px-6 py-3.5 rounded-full text-sm font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50">Back</button>
            <button
              onClick={() => setStep(4)}
              disabled={form.categories_selected.length === 0}
              className="flex-1 px-6 py-3.5 rounded-full text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: NDA & Agreements */}
      {step === 4 && (
        <div className="space-y-5">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>NDA & Agreements</h2>

          <div>
            <label className={labelCls}>Do you require inventors to sign an NDA before pitching?</label>
            <div className="flex gap-4 mt-2">
              <label className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 cursor-pointer ${form.requires_nda ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                <input type="radio" checked={form.requires_nda === true} onChange={() => update('requires_nda', true)} /> Yes
              </label>
              <label className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 cursor-pointer ${!form.requires_nda ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                <input type="radio" checked={form.requires_nda === false} onChange={() => update('requires_nda', false)} /> No
              </label>
            </div>
          </div>

          {form.requires_nda && (
            <div>
              <label className={labelCls}>NDA Link or Instructions</label>
              <textarea className={inputCls} value={form.nda_instructions} onChange={e => update('nda_instructions', e.target.value)} rows={3} placeholder="Paste your NDA link or provide instructions..." />
            </div>
          )}

          <div className="border-t border-gray-200 pt-5 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.sms_consent} onChange={e => update('sms_consent', e.target.checked)} className="mt-1 w-4 h-4 accent-amber-500" />
              <span className="text-sm text-gray-600">By entering your phone number, you consent to receive messages for this event via SMS. Message and data rates may apply. Reply STOP to opt out.</span>
            </label>

            {form.sms_consent && (
              <div>
                <label className={labelCls}>Phone Number</label>
                <input type="tel" className={inputCls} value={form.phone_number} onChange={e => update('phone_number', e.target.value)} placeholder="(555) 123-4567" />
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.terms_accepted} onChange={e => update('terms_accepted', e.target.checked)} className="mt-1 w-4 h-4 accent-amber-500" />
              <span className="text-sm text-gray-600">
                I accept the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-amber-600 underline font-medium">Terms & Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-600 underline font-medium">Privacy Policy</a>. I acknowledge that submission data may be used for AI-powered matching.
              </span>
            </label>
          </div>

          <div className="flex gap-3">
            <button onClick={() => setStep(3)} className="flex-1 px-6 py-3.5 rounded-full text-sm font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50">Back</button>
            <button
              onClick={handleSubmit}
              disabled={!form.terms_accepted || submitting}
              className="flex-1 px-6 py-3.5 rounded-full text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              {submitting ? 'Submitting...' : 'Confirm & Continue'}
            </button>
          </div>
        </div>
      )}

      {/* STEP 5: Sponsorship */}
      {step === 5 && submitted && hasSponsorshipStep && (
        <div className="space-y-5">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>Support This Event</h2>
          <p className="text-sm text-gray-500 mb-4">Choose a sponsorship level to gain visibility and support emerging toy creators.</p>

          <div className="space-y-4">
            {(event.sponsorship_tiers || []).map(tier => (
              <div key={tier.id} className="border-2 border-gray-200 rounded-2xl p-6 hover:border-amber-400 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>{tier.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{tier.description}</p>
                  </div>
                  <span className="text-xl font-bold text-amber-600">${(tier.price_cents / 100).toLocaleString()}</span>
                </div>
                {tier.perks?.length > 0 && (
                  <ul className="text-sm text-gray-600 space-y-1 mb-4">
                    {tier.perks.map((perk, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="text-amber-500">✓</span> {perk}
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  onClick={() => handleSponsorshipCheckout(tier)}
                  disabled={sponsorshipLoading}
                  className="w-full px-6 py-3 rounded-full text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
                >
                  {sponsorshipLoading ? 'Redirecting to checkout...' : `Become a ${tier.name}`}
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={() => setStep(6)}
            className="w-full text-center text-sm text-gray-400 hover:text-gray-600 transition-colors py-3"
          >
            Continue without sponsoring
          </button>
        </div>
      )}

      {/* STEP 6: Confirmation */}
      {step === 6 && submitted && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
            <div className="text-3xl mb-3">✓</div>
            <p className="text-emerald-700 font-semibold text-lg mb-1">You're All Set!</p>
            <p className="text-emerald-600 text-sm">Your registration is complete. We'll be in touch with next steps.</p>
          </div>

          <div className="bg-gray-50 rounded-2xl p-6">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">Registration Summary</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Company:</span> {form.company_name}</p>
              <p><span className="font-medium">Contact:</span> {form.first_name} {form.last_name}</p>
              <p><span className="font-medium">Email:</span> {form.email}</p>
              <p><span className="font-medium">Categories:</span> {form.categories_selected.join(', ')}</p>
              {form.requires_nda && <p><span className="font-medium">NDA:</span> Required</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
